import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const OrgContext = createContext(null);

export const OrgProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && user?.organizationId) {
      fetchOrgData();
    } else {
      setOrganization(null);
      setPlan(null);
      setUsage(null);
      setLoading(false);
    }
  }, [isAuthenticated, user?.organizationId]);

  const fetchOrgData = async () => {
    try {
      setLoading(true);

      // Fetch organization details
      const orgResponse = await api.get('/api/organizations/me');

      if (orgResponse.data.success) {
        const orgData = orgResponse.data.data;
        setOrganization(orgData);
        setPlan(orgData.planId);

        // Calculate usage from organization
        setUsage(orgData.usage || {
          users: 1,
          calls: 0,
          storage: 0,
          contacts: 0,
          campaigns: 0
        });
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const canUseFeature = (featureName) => {
    if (!plan?.features) return false;
    return plan.features[featureName] === true;
  };

  const checkLimit = (resource) => {
    if (!usage || !plan) {
      return { allowed: true, current: 0, limit: Infinity, remaining: Infinity };
    }

    const current = usage[resource] || 0;
    const limit = plan.limits?.[resource] ?? Infinity;

    return {
      allowed: current < limit,
      current,
      limit: limit === Infinity ? 'unlimited' : limit,
      remaining: limit === Infinity ? 'unlimited' : Math.max(0, limit - current),
      percentage: limit === Infinity ? 0 : (current / limit) * 100
    };
  };

  const refreshOrg = () => {
    fetchOrgData();
  };

  const value = {
    organization,
    plan,
    usage,
    loading,
    canUseFeature,
    checkLimit,
    refreshOrg,
    // Organization actions
    isTrial: organization?.status === 'trial',
    isActive: organization?.status === 'active',
    isSuspended: organization?.status === 'suspended',
    trialDaysRemaining: organization?.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(organization.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
      : 0
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

export default OrgContext;